"use client"

import { cn } from '@/shared/utils/tw'
import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { SpeakerIcon, MicIcon, SendIcon, ChatBubbleIcon, CallIcon } from '@/shared/ui/avatar-design-system/icons'

interface InputControlsProps {
  chatMode?: 'voice' | 'chat'
  onChatModeToggle?: () => void
  onSendMessage?: (message: string) => void
  isSpeakerOn?: boolean
  isMicOn?: boolean
  onToggleSpeaker?: () => void
  onToggleMic?: () => void
  blurStrength?: number // 0-100
  placeholder?: string
  voiceButtonText?: string
  chatButtonText?: string
  // 웹 검색 기능
  showSearchToggle?: boolean
  isSearchEnabled?: boolean
  onSearchToggle?: (enabled: boolean) => void
  // 배경 밝기에 따른 스타일 (chat 모드 입력바에 적용)
  variant?: 'dark' | 'light'
}

const InputControls: React.FC<InputControlsProps> = ({
  chatMode = 'voice',
  onChatModeToggle,
  onSendMessage,
  isSpeakerOn: isSpeakerOnProp,
  isMicOn: isMicOnProp,
  onToggleSpeaker,
  onToggleMic,
  placeholder: placeholderProp,
  voiceButtonText: voiceButtonTextProp,
  chatButtonText: chatButtonTextProp,
  showSearchToggle = false,
  isSearchEnabled = false,
  onSearchToggle,
  variant = 'dark',
}) => {
  const { t } = useTranslation('common')
  const isLightVariant = variant === 'light'

  // i18n 번역 텍스트 사용 (props가 제공되면 props 우선)
  const placeholder = placeholderProp ?? t('chat.input.placeholder')
  const voiceButtonText = voiceButtonTextProp ?? t('chat.mode.voice')
  const chatButtonText = chatButtonTextProp ?? t('chat.mode.text')
  // 외부에서 상태가 제공되지 않으면 내부 상태 사용
  const [internalSpeakerOn, setInternalSpeakerOn] = useState(true)
  const [internalMicOn, setInternalMicOn] = useState(false)

  const isSpeakerOn = isSpeakerOnProp ?? internalSpeakerOn
  const isMicOn = isMicOnProp ?? internalMicOn

  const [inputValue, setInputValue] = useState('')
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)
  const [isComposing, setIsComposing] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const isVoiceMode = chatMode === 'voice'
  const hasText = inputValue.trim().length > 0

  // 키보드 감지 (ai-companion-next/input-controls.tsx:62-86)
  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    const handleFocus = () => {
      window.scrollTo(0, 0)
      setIsKeyboardVisible(true)
    }

    const handleBlur = () => {
      window.scrollTo(0, 0)
      setIsKeyboardVisible(false)
    }

    textarea.addEventListener("focus", handleFocus)
    textarea.addEventListener("blur", handleBlur)

    return () => {
      textarea.removeEventListener("focus", handleFocus)
      textarea.removeEventListener("blur", handleBlur)
    }
  }, [isVoiceMode])

  // Textarea 자동 높이 조정 (ai-companion-next/input-controls.tsx:117-123)
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = "auto"
      textarea.style.height = `${textarea.scrollHeight}px`
    }
  }, [inputValue])

  // 메시지 전송 핸들러
  const handleSendMessage = () => {
    if (inputValue.trim() && !isComposing) {
      onSendMessage?.(inputValue.trim())
      setInputValue('')
      textareaRef.current?.blur()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <div
      className="absolute bottom-0 left-0 right-0 z-100 pointer-events-none"
      style={{
        padding: '3.72cqw',
        marginBottom: '20px',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div
        className={cn(
          "flex items-center justify-center",
          "mx-auto",
          "pointer-events-auto",
          "transition-[gap] duration-300 ease-in-out",
        )}
        style={{ gap: (isKeyboardVisible || !isVoiceMode) ? 0 : '1.86cqw' }}
      >
        {/* Speaker Button - always rendered, hidden with animation in chat mode */}
        <button
          type="button"
          onClick={() => {
            if (onToggleSpeaker) {
              onToggleSpeaker()
            } else {
              setInternalSpeakerOn(!internalSpeakerOn)
            }
          }}
          className={cn(
            "shrink-0",
            "rounded-[50px]",
            "backdrop-blur-[20px]",
            "[-webkit-backdrop-filter:blur(20px)]",
            "flex items-center justify-center",
            "cursor-pointer",
            "[transition:all_0.2s_ease,opacity_0.3s_ease,transform_0.3s_ease,width_0.3s_ease,margin_0.3s_ease]",
            "relative",
            isSpeakerOn
              ? "bg-[rgba(255,255,255,0.15)]"
              : "bg-white",
            "active:scale-95",
            (isKeyboardVisible || !isVoiceMode) && "opacity-0 m-0 p-0 overflow-hidden pointer-events-none"
          )}
          style={{
            width: (isKeyboardVisible || !isVoiceMode) ? 0 : '11.16cqw',
            height: (isKeyboardVisible || !isVoiceMode) ? 0 : '11.16cqw',
            borderWidth: (isKeyboardVisible || !isVoiceMode) ? 0 : '1.5px',
            borderColor: 'rgba(255,255,255,0.1)',
          }}
          aria-label="스피커 토글"
        >
          <SpeakerIcon style={{ width: '5.58cqw', height: '5.58cqw' }} isOn={isSpeakerOn} />
        </button>

        {/* Mic Button - always rendered, hidden with animation in chat mode */}
        <button
          type="button"
          onClick={() => {
            if (onToggleMic) {
              onToggleMic()
            } else {
              setInternalMicOn(!internalMicOn)
            }
          }}
          className={cn(
            "shrink-0",
            "rounded-[50px]",
            "backdrop-blur-[20px]",
            "[-webkit-backdrop-filter:blur(20px)]",
            "flex items-center justify-center",
            "cursor-pointer",
            "[transition:all_0.2s_ease,opacity_0.3s_ease,transform_0.3s_ease,width_0.3s_ease,margin_0.3s_ease]",
            "relative",
            isMicOn
              ? "bg-[rgba(255,255,255,0.15)]"
              : "bg-white",
            "active:scale-95",
            (isKeyboardVisible || !isVoiceMode) && "opacity-0 m-0 p-0 overflow-hidden pointer-events-none"
          )}
          style={{
            width: (isKeyboardVisible || !isVoiceMode) ? 0 : '11.16cqw',
            height: (isKeyboardVisible || !isVoiceMode) ? 0 : '11.16cqw',
            borderWidth: (isKeyboardVisible || !isVoiceMode) ? 0 : '1.5px',
            borderColor: 'rgba(255,255,255,0.1)',
          }}
          aria-label="마이크 토글"
        >
          <MicIcon style={{ width: '5.58cqw', height: '5.58cqw' }} isOn={isMicOn} />
        </button>

        {/* 음성 모드: 음성바 */}
        {isVoiceMode && (
          <div
            className={cn(
              "flex-1 min-w-0",
              "flex items-center justify-between",
              "bg-[rgba(255,255,255,0.15)]",
              "backdrop-blur-[20px]",
              "[-webkit-backdrop-filter:blur(20px)]",
              "transition-all duration-300 ease-in-out",
            )}
            style={{
              minHeight: '11.16cqw',
              maxHeight: '46.51cqw',
              borderWidth: '1.5px',
              borderColor: 'rgba(255,255,255,0.1)',
              borderStyle: 'solid',
              borderRadius: '11.63cqw',
              gap: '1.86cqw',
              paddingTop: '1.40cqw',
              paddingBottom: '1.40cqw',
              paddingRight: '1.40cqw',
              paddingLeft: isKeyboardVisible ? '4.19cqw' : '3.72cqw',
            }}
          >
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={() => setIsComposing(false)}
              placeholder={placeholder}
              rows={1}
              className={cn(
                "flex-1",
                "border-none bg-transparent",
                "font-['Pretendard',-apple-system,BlinkMacSystemFont,sans-serif]",
                "font-normal",
                "leading-normal",
                "text-white",
                "outline-none",
                "py-0 px-0",
                "min-w-0",
                "resize-none",
                "overflow-y-hidden",
                "[word-wrap:break-word] whitespace-pre-wrap",
                "placeholder:text-white/60",
                "[&::-webkit-scrollbar]:w-1",
                "[&::-webkit-scrollbar-track]:bg-transparent",
                "[&::-webkit-scrollbar-thumb]:bg-white/30 [&::-webkit-scrollbar-thumb]:rounded-sm",
                "[&::-webkit-scrollbar-thumb:hover]:bg-white/50"
              )}
              style={{ fontSize: '14px', maxHeight: '41.86cqw', minHeight: '4.88cqw' }}
              aria-label="메시지 입력"
            />

            {/* 키보드 또는 텍스트 있으면: 전송 버튼 / 아니면: 채팅 버튼 */}
            {(isKeyboardVisible || hasText) ? (
              <button
                type="button"
                onClick={handleSendMessage}
                disabled={!hasText}
                className={cn(
                  "shrink-0",
                  "self-center",
                  "rounded-[50px]",
                  "border-none",
                  "flex items-center justify-center",
                  "cursor-pointer",
                  "transition-all duration-200",
                  "p-0",
                  "bg-white",
                  "active:scale-95"
                )}
                style={{ width: '32px', height: '32px' }}
                aria-label="메시지 전송"
              >
                <SendIcon
                  className={cn(
                    "transition-colors duration-200"
                  )}
                  style={{ width: '13px', height: '15px' }}
                  hasInput={true}
                />
              </button>
            ) : (
              <button
                type="button"
                onClick={onChatModeToggle}
                className={cn(
                  "flex items-center justify-center",
                  "bg-white",
                  "rounded-[50px]",
                  "cursor-pointer",
                  "transition-all duration-200",
                  "active:scale-95"
                )}
                style={{
                  paddingLeft: '2.33cqw',
                  paddingRight: '2.33cqw',
                  paddingTop: '1.86cqw',
                  paddingBottom: '1.86cqw',
                  height: '32px',
                  gap: '0.47cqw',
                }}
                aria-label="채팅 모드로 전환"
              >
                <ChatBubbleIcon style={{ width: '3.72cqw', height: '3.72cqw' }} className="text-[#222222]" />
                <span className="font-['Pretendard',-apple-system,BlinkMacSystemFont,sans-serif] font-semibold text-[#222222]" style={{ fontSize: '14px' }}>
                  {chatButtonText}
                </span>
              </button>
            )}
          </div>
        )}

        {/* 채팅 모드: 검색 토글 버튼 */}
        {!isVoiceMode && showSearchToggle && (
          <button
            type="button"
            onClick={() => onSearchToggle?.(!isSearchEnabled)}
            className={cn(
              "shrink-0",
              "flex items-center justify-center",
              "rounded-full",
              "border",
              "transition-all duration-200",
              "active:scale-95",
              isSearchEnabled
                ? "bg-blue-500/20 border-blue-500/50"
                : "bg-[rgba(255,255,255,0.15)] border-white/10",
            )}
            style={{
              paddingLeft: '2.79cqw',
              paddingRight: '2.79cqw',
              paddingTop: '1.86cqw',
              paddingBottom: '1.86cqw',
              height: '9.30cqw',
              gap: '0.93cqw',
            }}
            title={isSearchEnabled ? "웹 검색 끄기" : "웹 검색 켜기"}
          >
            <svg
              className={cn(
                isSearchEnabled ? "text-blue-400" : "text-white/60"
              )}
              style={{ width: '3.72cqw', height: '3.72cqw' }}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <span className={cn(
              "font-medium",
              isSearchEnabled ? "text-blue-400" : "text-white/60"
            )} style={{ fontSize: '14px' }}>
              {isSearchEnabled ? "ON" : "OFF"}
            </span>
          </button>
        )}

        {/* 채팅 모드: 채팅바 */}
        {!isVoiceMode && (
          <div
            className={cn(
              "flex-1 min-w-0",
              "flex items-center justify-between",
              isLightVariant ? "bg-[rgba(0,0,0,0.04)]" : "bg-[rgba(255,255,255,0.15)]",
              "backdrop-blur-[20px]",
              "[-webkit-backdrop-filter:blur(20px)]",
              "transition-all duration-300 ease-in-out",
            )}
            style={{
              minHeight: '11.16cqw',
              maxHeight: '46.51cqw',
              borderWidth: '1.5px',
              borderColor: isLightVariant ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)',
              borderStyle: 'solid',
              borderRadius: '11.63cqw',
              gap: '1.86cqw',
              paddingTop: '1.40cqw',
              paddingBottom: '1.40cqw',
              paddingRight: '1.40cqw',
              paddingLeft: '4.19cqw',
            }}
          >
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={() => setIsComposing(false)}
              placeholder={placeholder}
              rows={1}
              className={cn(
                "flex-1",
                "border-none bg-transparent",
                "font-['Pretendard',-apple-system,BlinkMacSystemFont,sans-serif]",
                "font-normal",
                "leading-normal",
                isLightVariant ? "text-[#222222]" : "text-white",
                "outline-none",
                "py-0 px-0",
                "min-w-0",
                "resize-none",
                "overflow-y-hidden",
                "[word-wrap:break-word] whitespace-pre-wrap",
                isLightVariant ? "placeholder:text-black/40" : "placeholder:text-white/60",
                "[&::-webkit-scrollbar]:w-1",
                "[&::-webkit-scrollbar-track]:bg-transparent",
                isLightVariant
                  ? "[&::-webkit-scrollbar-thumb]:bg-black/30 [&::-webkit-scrollbar-thumb]:rounded-sm [&::-webkit-scrollbar-thumb:hover]:bg-black/50"
                  : "[&::-webkit-scrollbar-thumb]:bg-white/30 [&::-webkit-scrollbar-thumb]:rounded-sm [&::-webkit-scrollbar-thumb:hover]:bg-white/50"
              )}
              style={{ fontSize: '14px', maxHeight: '41.86cqw', minHeight: '4.88cqw' }}
              aria-label="메시지 입력"
            />

            {/* 키보드 또는 텍스트 있으면: 전송 버튼 / 아니면: 음성 버튼 */}
            {(isKeyboardVisible || hasText) ? (
              <button
                type="button"
                onClick={handleSendMessage}
                disabled={!hasText}
                className={cn(
                  "shrink-0",
                  "self-center",
                  "rounded-[50px]",
                  "border-none",
                  "flex items-center justify-center",
                  "cursor-pointer",
                  "transition-all duration-200",
                  "p-0",
                  "bg-white",
                  "active:scale-95"
                )}
                style={{ width: '32px', height: '32px' }}
                aria-label="메시지 전송"
              >
                <SendIcon
                  className={cn(
                    "transition-colors duration-200"
                  )}
                  style={{ width: '13px', height: '15px' }}
                  hasInput={true}
                />
              </button>
            ) : (
              <button
                type="button"
                onClick={onChatModeToggle}
                className={cn(
                  "flex items-center justify-center",
                  "bg-white",
                  "rounded-[50px]",
                  "cursor-pointer",
                  "transition-all duration-200",
                  "active:scale-95"
                )}
                style={{
                  paddingLeft: '2.33cqw',
                  paddingRight: '2.33cqw',
                  paddingTop: '1.86cqw',
                  paddingBottom: '1.86cqw',
                  height: '32px',
                  gap: '0.47cqw',
                }}
                aria-label="음성 모드로 전환"
              >
                <CallIcon style={{ width: '4.19cqw', height: '4.19cqw' }} className="text-[#222222]" />
                <span className="font-['Pretendard',-apple-system,BlinkMacSystemFont,sans-serif] font-semibold text-[#222222]" style={{ fontSize: '14px' }}>
                  {voiceButtonText}
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default InputControls
